/**
 * @file district-wise-order-trend.tsx
 * @module app/modules/sales-management/sales-representative/dashboard/chart/district-wise-order-trend
 * @description Implements the district wise order trend interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.5
 */

import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Method from "../../../../../../utils/methods";
import APICallService from "../../../../../../api/apiCallService";
import { salesDashboard } from "../../../../../../api/apiEndPoints";
import Dashboard from "../dashboard";
import Loader from "../../../../../../Global/loader";

const DistrictWiseOrderTrend: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<any>(new Date());
  const [categories, setCategories] = useState<string[]>([]);
  const [seriesData, setSeriesData] = useState<any[]>([
    {
      name: "Orders",
      data: [],
    },
  ]);

  useEffect(() => {
    const firstDay = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const lastDay = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    );
    fetchReports(firstDay, lastDay, undefined);
  }, [startDate]);

  const fetchReports = async (fromDate: any, toDate: any, district: any) => {
    setLoading(true);
    let params: any = {
      fromDate: Method.convertDateToFormat(fromDate, "YYYY-MM-DD"),
      toDate: Method.convertDateToFormat(toDate, "YYYY-MM-DD"),
      // utcOffset: new Date().getTimezoneOffset(),
      // reportType: "0",
    };
    if (district !== undefined) {
      params = { ...params, "districts[]": district._id };
    }
    // if (selectedWarehouse?.value) {
    //   params = {
    //     ...params,
    //     warehouse: selectedWarehouse?.value,
    //   };
    // }
    let apiService = new APICallService(
      salesDashboard.districtOrderTrendReport,
      params,
      "",
      "",
      "",
      "",
      Dashboard
    );
    let response = await apiService.callAPI();
    if (response && response.length) {
      // Aggregate orders per district and bind to chart
      const map: Record<string, number> = {};
      (response || []).forEach((item: any) => {
        const name = item?.address?.districtName || item?.districtName || "Unknown";
        const orders = Number(item.totalOrders ?? 0);
        map[name] = (map[name] || 0) + orders;
      });
      const cats = Object.keys(map);
      const data = cats.map((c) => map[c]);
      setCategories(cats);
      setSeriesData([
        {
          name: "Orders",
          data,
        },
      ]);
    } else {
      setCategories([]);
      setSeriesData([
        {
          name: "Orders",
          data: [],
        },
      ]);
    }
    setLoading(false);
  };

  return (
    <Col sm={12} md={6} lg={6}>
      <div
        className="bg-f9f9f9 px-5 py-5"
        style={{ border: "0.5px solid #E0E0DF", borderRadius: "10px", height: "455px" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0 fw-bolder fs-18">District-wise delivered orders trend</h3>
          <div className="w-150px">
            <DatePicker
              selected={startDate}
              onChange={(date: Date) => setStartDate(date)}
              dateFormat="MMM yyyy"
              showMonthYearPicker
              className="form-control form-control-solid bg-white border-gray-300 cursor-pointer"
              calendarClassName="sales-trend-month-picker"
              maxDate={new Date()}
            />
          </div>
        </div>

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "275px" }}
          >
            <Loader loading={loading} />
          </div>
        ) : (
          <Chart options={{
            chart: {
              type: "bar",
              height: 313,
              toolbar: { show: false },
            },
            plotOptions: {
              bar: {
                columnWidth: "40%",
                borderRadius: 6,
              },
            },
            dataLabels: { enabled: false },
            grid: {
              strokeDashArray: 6,
              borderColor: "#E5E7EB",
            },
            xaxis: {
              categories: categories,
              labels: {
                style: { fontSize: "13px", colors: "#6B7280" },
              },
            },
            yaxis: {
              tickAmount: 3,
              labels: {
                style: { fontSize: "12px", colors: "#6B7280" },
              },
            },
            states: {
              normal: { filter: { type: 'none', value: 0 } },
              hover: { filter: { type: 'none', value: 0 } },
              active: { allowMultipleDataPointsSelection: false, filter: { type: 'none', value: 0 } },
            },
            tooltip: {
              style: { fontSize: '13px' },
            },
            colors: ["#6366F1"],
          }}
            series={seriesData}
            type="bar"
            height="350"
          />
        )}
      </div>
    </Col>
  );
};

export default DistrictWiseOrderTrend;
